import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNextItemByKey } from 'utils/common';

import { getUserIsAuthenticated } from 'reduxModules/user/selector';
import { updateLiveLessonProgress } from 'reduxModules/live/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { push } from 'reduxModules/navigation/action';

import { getLiveFilterInfo } from 'reduxModules/live/selector';

import usePlaylist from 'hooks/usePlaylist';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import { LIVE_TOPIC_LESSON_TEMPLATE } from 'constants/routingPath';
import { LIVE_TOPIC } from 'constants/contentTypes';
import {
  REGULAR_CLASS_EPISODE_PLAY,
  REGULAR_CLASS_EPISODE_END,
  REGULAR_CLASS_EPISODE_CLICK,
} from 'constants/gtmEvent';
import { RC } from 'constants/tracingEvent';
import useGetCurrentDevice from 'hooks/useGetCurrentDevice';

export default function useRCPlaylist({
  freeLessonList,
  paidLessonList,
  initialLessonId,
  liveTopicId,
  liveTopicName,
  defaultMuted,
  instructorIdList,
  instructorNameList,
}) {
  const dispatch = useDispatch();
  const getOfficialSiteURL = useGetOfficialSiteURL();
  const { isMobile } = useGetCurrentDevice();
  const isAuthenticated = useSelector(getUserIsAuthenticated);
  const { selectedGradeLevel } = useSelector(getLiveFilterInfo);
  const playerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(defaultMuted);

  const lessonPlaylist = useMemo(() => [...freeLessonList, ...paidLessonList], [
    freeLessonList,
    paidLessonList,
  ]);

  const getWatchable = useCallback(
    currentLesson => currentLesson.watchEnabled,
    [],
  );

  const getGtmEventData = useCallback(
    currentLesson => ({
      gradeLevelName: selectedGradeLevel.name,
      topicId: liveTopicId,
      topicName: liveTopicName,
      instructorId: instructorIdList,
      instructorName: instructorNameList,
      liveTime: currentLesson.startAt,
      duration: currentLesson.videoDuration,
      episodeStatus: currentLesson.status,
      episodeId: currentLesson.id,
      episodeName: currentLesson.name,
      watchable: currentLesson.watchEnabled,
      section: LIVE_TOPIC,
    }),
    [
      liveTopicId,
      liveTopicName,
      selectedGradeLevel,
      instructorIdList,
      instructorNameList,
    ],
  );

  const onPlayCurrentLesson = useCallback(
    currentLesson => {
      dispatch(
        sendGTMEvent({
          eventName: REGULAR_CLASS_EPISODE_PLAY,
          data: getGtmEventData(currentLesson),
        }),
      );
    },
    [dispatch, getGtmEventData],
  );

  const {
    currentLesson,
    setCurrentLesson,
    nextLesson,
    playCurrentLesson,
    pauseCurrentLesson,
    playNextLesson,
    onEndedLesson,
    showAutoPlayController,
    getIsCurrent,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
    onSeekEnd,
  } = usePlaylist({
    type: RC,
    playerRef,
    id: liveTopicId,
    initialLessonId,
    lessonPlaylist,
    getWatchable,
    onPlayCurrentLesson,
  });

  const nextFreeLesson = useMemo(
    () => getNextItemByKey(freeLessonList, currentLesson, 'id'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [freeLessonList, currentLesson.id],
  );

  const isLastFreeLesson = useMemo(
    () => currentLesson.id === nextFreeLesson?.id,
    [currentLesson.id, nextFreeLesson?.id],
  );

  const onClickLesson = useCallback(
    lesson => {
      if (!getIsCurrent(lesson)) {
        dispatch(
          push(
            getOfficialSiteURL(LIVE_TOPIC_LESSON_TEMPLATE, {
              topicId: liveTopicId,
              lessonId: lesson.id,
            }),
          ),
        );

        if (lesson.isFree) {
          dispatch(
            sendGTMEvent({
              eventName: REGULAR_CLASS_EPISODE_CLICK,
              data: getGtmEventData(lesson),
            }),
          );
        }

        return setCurrentLesson(lesson);
      }

      if (!playerRef.current) return null;

      // if lesson is current one, play it if it is pausing, pause it if it is playing
      return currentLesson.isPlaying
        ? playerRef.current.handlePause()
        : playerRef.current.handlePlay();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentLesson,
      getIsCurrent,
      dispatch,
      getOfficialSiteURL,
      liveTopicId,
      setCurrentLesson,
    ],
  );

  const playFirstFreeLesson = () => {
    setCurrentLesson(freeLessonList[0]);
  };

  const updateLessonProgress = useCallback(
    ({ currentTime, seekType }) => {
      onSeekEnd({ currentTime, seekType });

      if (!isAuthenticated || currentTime <= currentLesson.progressDuration)
        return;

      dispatch(
        updateLiveLessonProgress({
          lessonId: currentLesson.id,
          progressDuration: currentTime,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentLesson.id,
      currentLesson.progressDuration,
      dispatch,
      isAuthenticated,
    ],
  );

  const onEndedCurrentLesson = useCallback(
    ({ currentTime }) => {
      onEndedLesson({
        currentTime,
        isHideNextVideoController: isLastFreeLesson,
      });

      if (!isAuthenticated || isLastFreeLesson) return;

      dispatch(
        updateLiveLessonProgress({
          lessonId: currentLesson.id,
          progressDuration: currentLesson.videoDuration,
        }),
      );

      dispatch(
        sendGTMEvent({
          eventName: REGULAR_CLASS_EPISODE_END,
          data: getGtmEventData(currentLesson),
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      currentLesson.id,
      currentLesson.videoDuration,
      isAuthenticated,
      isLastFreeLesson,
      getGtmEventData,
      onEndedLesson,
    ],
  );

  const onVolumechange = useCallback(() => {
    setIsMuted(playerRef.current.playerRef.current.muted());
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsMuted(false);
    }
  }, [isMobile]);

  return {
    isMuted,
    currentLesson,
    onClickLesson,
    onEndedLesson: onEndedCurrentLesson,
    onVolumechange,
    updateLessonProgress,
    pauseCurrentLesson,
    playCurrentLesson,
    playFirstFreeLesson,
    playerRef,
    playNextLesson,
    showAutoPlayController,
    nextLesson,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
  };
}
